import type { User } from "@safers/api";
import { canAccessMenuSite, hasMenu, hasNoSiteAccess } from "@safers/auth";
import { AREA_MENU, AREAS, areaPath, type Area } from "@/routes/paths";

const AREA_KEY = (username: string) => `safers.last-area:${username}`;
const SITE_KEY = (username: string) => `safers.last-site:${username}`;

function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    return;
  }
}

function remove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    return;
  }
}

export function readLastArea(username: string | null): Area | null {
  if (!username) return null;
  const raw = read(localStorage, AREA_KEY(username));
  return AREAS.includes(raw as Area) ? (raw as Area) : null;
}

export function writeLastArea(username: string | null, area: Area): void {
  if (!username) return;
  write(localStorage, AREA_KEY(username), area);
}

export function readLastSite(username: string | null): string | null {
  if (!username) return null;
  return read(sessionStorage, SITE_KEY(username));
}

export function writeLastSite(username: string | null, siteId: string): void {
  if (!username) return;
  write(sessionStorage, SITE_KEY(username), siteId);
}

export function clearLastVisit(username: string | null): void {
  if (!username) return;
  remove(localStorage, AREA_KEY(username));
  remove(sessionStorage, SITE_KEY(username));
}

function canEnter(user: User | null, area: Area): boolean {
  return !hasNoSiteAccess(user) && hasMenu(user, AREA_MENU[area]);
}

/** 권한 있는 첫 영역의 처음 주소(관제 → 보건 → CCTV). 들어갈 영역이 없으면 `null`. */
export function homePath(user: User | null): string | null {
  const area = AREAS.find((a) => canEnter(user, a));
  return area ? areaPath(area) : null;
}

/**
 * 로그인 직후 주소. 마지막으로 머문 영역·현장으로 돌아가되, 그 사이 권한이 없어졌으면
 * 기억을 버리고 `homePath` 를 따른다.
 */
export function landingPath(user: User | null): string | null {
  const username = user?.username ?? null;
  const area = readLastArea(username);
  if (!area || !canEnter(user, area)) return homePath(user);
  const site = readLastSite(username);
  const keepSite = site != null && canAccessMenuSite(user, site, AREA_MENU[area]);
  return areaPath(area, keepSite ? site : null);
}
